import * as THREE from 'three';

// Renders lyric-hook.js's up-to-3 most-repeated word-sets as bold
// flashing text, timed to the synced lyric lines - "positionner cette
// partie de paroles en fond d'ecran (derriere les animations) [...]
// elles apparaissent et disparaissent en fonction du timing". Owned
// centrally by scene-manager.js (one instance, re-attached to whatever
// THREE.Scene is current) rather than duplicated per-visualizer, so this
// applies uniformly across all of them for free.
//
// "Derriere les animations [...] derriere les RIGS, derriere la
// pochette" is real depth occlusion, not a DOM layer or z-index trick:
// the text is drawn onto a THREE.CanvasTexture, cut out with alphaTest
// (opaque letter pixels only - nothing between them), and placed on a
// normal opaque-for-depth-purposes plane far along the camera's own view
// direction - deliberately much farther than any visualizer's own
// content (rigs/particles/artwork all live within roughly a dozen units
// of the origin; TEXT_DEPTH below leaves a wide margin beyond that,
// still well inside the camera's own far clip plane). Three's default
// render order (opaque objects write depth first; every visualizer's own
// additive/transparent content is depth-tested, not depth-writing, per
// this codebase's existing convention) then makes every animation's own
// shapes correctly cover the text wherever they pass in front, with zero
// per-visualizer coordination - the technique a 3D-embedded artwork mesh
// had already proven, just centralized here.
//
// Positioned via the camera's live right/up/forward basis (not a fixed
// world-space offset) and recomputed every frame, so it tracks
// scene-manager's own camera drift and each visualizer's own camera
// framing automatically, the same "camera-agnostic, recomputed live"
// approach the 3D artwork alignment had settled on.

// Comfortably past every visualizer's own content (rigs/fields top out
// around radius ~7, ring patterns a bit further but still flat near
// Z=0) while staying well inside the shared camera's far=100 clip plane
// even with camera.position.z ~6-9 and drift on top - "il doit toujours
// apparaitre derriere les rigs, derriere la pochette, en fond de TOUTES
// les animations" needed a much bigger margin than this file's first cut.
const TEXT_DEPTH = 45;
const SCREEN_HEIGHT_FRACTION_MIN = 0.13;
const SCREEN_HEIGHT_FRACTION_MAX = 0.27;
// Keeps the random placement within this fraction of the half-frustum
// extent at TEXT_DEPTH, so a big phrase at an extreme random offset
// still can't clip off-screen.
const SAFE_X_FRACTION = 0.6;
const SAFE_Y_FRACTION = 0.55;

// party-mode.html's #overlay-artwork always renders in a DOM layer above
// the WebGL canvas via plain CSS stacking, completely independent of
// this scene's own depth buffer - true depth occlusion "derriere la
// pochette" is only physically possible where the cover ALSO exists as
// real scene geometry (an embedded artwork mesh).
// Everywhere else, the practical equivalent is just never placing text
// where the cover already sits, in screen-space NDC - measured from the
// same #overlay-artwork getBoundingClientRect() this app's other
// alignment work already used (roughly centered, ~35% of the frame
// wide/tall at 16:9), padded outward for margin/title-text below it.
const ARTWORK_EXCLUSION_X = 0.24;
const ARTWORK_EXCLUSION_Y_MIN = -0.32;
const ARTWORK_EXCLUSION_Y_MAX = 0.46;
const ARTWORK_EXCLUSION_RETRIES = 12;

// Oversampled relative to CANVAS_FONT_PX's own logical size so the
// alphaTest cutout edge (see below) is defined from crisp, densely-
// sampled source pixels rather than a soft anti-aliased boundary - "les
// contours trop flous" was this canvas being drawn at a coarser
// resolution than the glyphs needed for a hard edge.
const CANVAS_FONT_PX = 220;
const CANVAS_SUPERSAMPLE = 2;
const CANVAS_PADDING_X = CANVAS_FONT_PX * 0.18;
const CANVAS_PADDING_Y = CANVAS_FONT_PX * 0.3;
const FONT_FAMILY = "'Arial Black', 'Helvetica Neue', Arial, sans-serif";

// A tighter cutout than a plain 0.5 mid-alpha threshold - discards more
// of the antialiased rim around each glyph, leaving only the solidly-
// opaque core, which reads as a crisp edge instead of a soft one once
// bloom (even at BLOOM-safe brightness, see TEXT_LINEAR_BRIGHTNESS) adds
// its own bit of spread on top.
const ALPHA_TEST = 0.75;

// A flat MeshBasicMaterial with a pure-white (linear 1.0) texture sailed
// straight through the bloom pass's own 0.78 luminosity threshold
// (scene-manager.js's UnrealBloomPass) - "trop de glow [...] trop
// eclaire" - the same failure mode a white-cover 3D artwork had,
// fixed the same way: keep the material's own linear output
// under that threshold with a safety margin, via material.color rather
// than a shader (this text has no photo-hue to preserve, so a flat
// dim multiply is enough). THREE.NoColorSpace on setRGB is deliberate -
// this is a raw linear-space cap compared directly against the bloom
// shader's own linear threshold, not a color meant to go through sRGB
// interpretation.
const TEXT_LINEAR_BRIGHTNESS = 0.6;

const BEAT_FLASH_AMOUNT = 0.16;
const BEAT_FLASH_DECAY = 7;

const _worldUp = new THREE.Vector3(0, 1, 0);

export class LyricFlashLayer {
  constructor() {
    this._canvas = document.createElement('canvas');
    this._canvas.width = 2;
    this._canvas.height = 2;
    this._ctx = this._canvas.getContext('2d');
    this._texture = new THREE.CanvasTexture(this._canvas);
    this._texture.colorSpace = THREE.SRGBColorSpace;
    this._textAspect = 1;

    const geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.MeshBasicMaterial({
      map: this._texture,
      // Cutout, not blended - the empty (fully transparent) area of the
      // plane writes nothing at all (no color, no depth), only the
      // opaque letter pixels do, so occlusion behaves correctly and
      // there's no big invisible-but-depth-writing rectangle blocking
      // whatever else happens to sit behind those margins.
      transparent: false,
      alphaTest: ALPHA_TEST,
      depthWrite: true,
      depthTest: true,
      fog: false,
      toneMapped: false,
      side: THREE.FrontSide
    });
    this._material.color.setRGB(TEXT_LINEAR_BRIGHTNESS, TEXT_LINEAR_BRIGHTNESS, TEXT_LINEAR_BRIGHTNESS, THREE.NoColorSpace);
    this._mesh = new THREE.Mesh(geometry, this._material);
    this._mesh.visible = false;
    this._mesh.frustumCulled = false; // the plane's own local bounds don't reflect the actual glyph footprint closely enough to trust culling here

    this._scene = null;
    // Flattened, time-sorted list of every occurrence across all of
    // lyric-hook.js's (up to 3) selected phrases - [{phrase, time,
    // endTime}, ...] - so "which phrase is active right now" is one
    // linear scan regardless of how many distinct hooks are in play.
    this._entries = [];
    this._lastHooksRef = undefined;
    this._activeIndex = -1;
    this._ndcX = 0;
    this._ndcY = 0;
    this._baseWorldWidth = 1;
    this._baseWorldHeight = 1;

    this._dir = new THREE.Vector3();
    this._negDir = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._up = new THREE.Vector3();
    this._center = new THREE.Vector3();
    this._basis = new THREE.Matrix4();
  }

  setScene(scene) {
    if (this._scene === scene) return;
    if (this._scene) this._scene.remove(this._mesh);
    this._scene = scene;
    if (scene) scene.add(this._mesh);
    this._activeIndex = -1;
    this._mesh.visible = false;
  }

  // Flattens lyric-hook.js's [{ phrase, occurrences }, ...] result (up to
  // 3 entries) into this._entries. Only actually re-runs when hooks is a
  // different array than last time (see update()'s own comment on why
  // that check has to happen here, every frame, rather than in some
  // one-time setter).
  _applyHooks(hooks) {
    this._entries = [];
    if (Array.isArray(hooks)) {
      for (let h = 0; h < hooks.length; h++) {
        const hook = hooks[h];
        for (let i = 0; i < hook.occurrences.length; i++) {
          this._entries.push({ phrase: hook.phrase, time: hook.occurrences[i].time, endTime: hook.occurrences[i].endTime });
        }
      }
      this._entries.sort(function (a, b) { return a.time - b.time; });
    }
    this._activeIndex = -1;
    this._mesh.visible = false;
  }

  // hooks: profile.lyricHooks, passed in FRESH every frame rather than
  // cached from some earlier setProfile-time snapshot. main.js's async
  // lyrics lookup resolves by mutating the shared profile object in
  // place, well after scene-manager.js's setProfile last ran for it - a
  // one-time "hand me the array once, remember it" setter never sees
  // that later mutation (the reference comparison it would need ends up
  // comparing two already-mutated views of the same object by the time
  // the next poll rolls around). Reading it here instead, every frame,
  // is what actually catches the update promptly - the reference-
  // equality check just moves from a rare one-time call site to a cheap
  // per-frame one.
  //
  // estimatedPosition: seconds into the track (scene-manager's own live
  // extrapolation, same source _updateBeatPhase uses) - null/not-playing
  // just hides the text rather than freezing it mid-flash.
  update(camera, hooks, estimatedPosition, playing, beatPhase) {
    if (hooks !== this._lastHooksRef) {
      this._lastHooksRef = hooks;
      this._applyHooks(hooks);
    }

    if (this._entries.length === 0 || !this._scene || !playing || estimatedPosition == null) {
      if (this._mesh.visible) this._mesh.visible = false;
      this._activeIndex = -1;
      return;
    }

    let idx = -1;
    for (let i = 0; i < this._entries.length; i++) {
      if (estimatedPosition >= this._entries[i].time && estimatedPosition < this._entries[i].endTime) {
        idx = i;
        break;
      }
    }

    if (idx === -1) {
      this._mesh.visible = false;
      this._activeIndex = -1;
      return;
    }

    if (idx !== this._activeIndex) {
      this._activeIndex = idx;
      this._relayout(camera, this._entries[idx].phrase);
    }

    this._mesh.visible = true;
    const pulse = beatPhase == null ? 1 : 1 + BEAT_FLASH_AMOUNT * Math.exp(-beatPhase * BEAT_FLASH_DECAY);
    this._mesh.scale.set(this._baseWorldWidth * pulse, this._baseWorldHeight * pulse, 1);
    this._placeMesh(camera);
  }

  // New random position + font size + freshly-drawn texture each time a
  // new occurrence starts - "les position et les tailles de police
  // changent a chaque fois".
  _relayout(camera, phrase) {
    this._drawText(phrase);

    const fontFraction = SCREEN_HEIGHT_FRACTION_MIN + Math.random() * (SCREEN_HEIGHT_FRACTION_MAX - SCREEN_HEIGHT_FRACTION_MIN);
    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeightAtDepth = TEXT_DEPTH * Math.tan(vFovRad / 2);
    this._baseWorldHeight = fontFraction * halfHeightAtDepth * 2;
    this._baseWorldWidth = this._baseWorldHeight * this._textAspect;

    // Reject-and-retry out of the DOM artwork's own screen region (see
    // ARTWORK_EXCLUSION_* above) rather than accepting whatever the
    // first random draw gives - a plain few-attempt retry is simpler
    // than solving for a valid annulus/band directly, and cheap since
    // this only runs once per new occurrence, not every frame.
    for (let attempt = 0; attempt < ARTWORK_EXCLUSION_RETRIES; attempt++) {
      this._ndcX = (Math.random() * 2 - 1) * SAFE_X_FRACTION;
      this._ndcY = (Math.random() * 2 - 1) * SAFE_Y_FRACTION;
      const insideArtwork = Math.abs(this._ndcX) < ARTWORK_EXCLUSION_X &&
        this._ndcY > ARTWORK_EXCLUSION_Y_MIN && this._ndcY < ARTWORK_EXCLUSION_Y_MAX;
      if (!insideArtwork) break;
    }
  }

  _drawText(phrase) {
    const ctx = this._ctx;
    const fontPx = CANVAS_FONT_PX * CANVAS_SUPERSAMPLE;
    ctx.font = '900 ' + fontPx + 'px ' + FONT_FAMILY;
    const metrics = ctx.measureText(phrase);
    const textWidth = Math.max(1, Math.ceil(metrics.width));
    const textHeight = Math.ceil(fontPx * 1.15);
    const canvasWidth = textWidth + CANVAS_PADDING_X * CANVAS_SUPERSAMPLE * 2;
    const canvasHeight = textHeight + CANVAS_PADDING_Y * CANVAS_SUPERSAMPLE * 2;

    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    // Resizing a canvas resets its 2D context state, so the font has to
    // be re-applied after setting width/height, not just once above.
    ctx.font = '900 ' + fontPx + 'px ' + FONT_FAMILY;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(phrase, canvasWidth / 2, canvasHeight / 2);

    this._texture.needsUpdate = true;
    this._textAspect = canvasWidth / canvasHeight;
  }

  _placeMesh(camera) {
    camera.getWorldDirection(this._dir);
    this._right.crossVectors(this._dir, _worldUp).normalize();
    this._up.crossVectors(this._right, this._dir).normalize();

    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeightAtDepth = TEXT_DEPTH * Math.tan(vFovRad / 2);
    const halfWidthAtDepth = halfHeightAtDepth * camera.aspect;

    this._center.copy(camera.position).addScaledVector(this._dir, TEXT_DEPTH);
    this._mesh.position.copy(this._center)
      .addScaledVector(this._right, this._ndcX * halfWidthAtDepth)
      .addScaledVector(this._up, this._ndcY * halfHeightAtDepth);

    this._negDir.copy(this._dir).negate();
    this._basis.makeBasis(this._right, this._up, this._negDir);
    this._mesh.quaternion.setFromRotationMatrix(this._basis);
  }
}
